import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  TextField,
  FontIcon,
  TooltipHost,
  Callout,
  DirectionalHint,
} from "@fluentui/react";
import { useBoolean, useId } from "@fluentui/react-hooks";
import { useWindowDimensions } from "ss-lib";
import { updateCRMJournal } from "../../../actions/CRM";
import { useSpring, a, config } from "react-spring";

const CRMJournalItem = ({ item, active, handleActive, user }) => {
  const { width } = useWindowDimensions();
  const [activeTab, setActiveTab] = useState(0);
  const [activeItem, setActiveItem] = useState(false);
  const [collapsed, setCollapsed] = useState(active === item ? false : true);
  const [inputOpen, setInputOpen] = useState(false);
  const [update, setUpdate] = useState(item);
  const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] =
    useBoolean(false);
  const buttonId = useId("callout-button");
  const labelId = useId("callout-label");

  const spring = useSpring({
    marginTop: collapsed ? 0 : 10,
    height: collapsed ? 0 : "fit-content",
    padding: collapsed ? 0 : 10,
    display: !collapsed ? "block" : "none",
    config: config.stiff,
  });

  const items = [
    "email",
    "url",
    "format",
    "submissions",
    "expedite",
    "withdraw",
    "librarians",
  ];

  useEffect(() => {
    active !== item && setCollapsed(true);
  }, [active, setCollapsed, item]);

  const handleChangeTab = (e) => {
    e.preventDefault();
    console.log(active);
    setActiveTab(e.target.id);
    setInputOpen(false);
    toggleIsCalloutVisible(false);

    if (active) {
      setCollapsed(false);
      handleActive(item);
      setActiveItem(true);
    } else if (activeTab !== e.target.id) {
      setCollapsed(false);
    } else if (activeTab === e.target.id) {
      setCollapsed(!collapsed);
      handleActive(item);
      setActiveTab(null);
      setActiveItem(false);
    }

    console.log(`Is active tab: ${activeTab}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setUpdate({ ...update, email: update.email });
    updateCRMJournal(user, update, setInputOpen);
  };

  const handleChange = (e) => {
    e.preventDefault();

    setUpdate({
      ...update,
      [e.target.name]: e.target.value,
    });

    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const JournalIcon = ({ iconName, label, index }) => {
    return (
      <FontIcon
        iconName={iconName}
        className="crm-icons crm-journal-icons"
        ariaLabel={label}
        id={index}
        data-tooltip-id={label}
        onClick={handleChangeTab}
        style={{ fontSize: width < 500 && 18 }}
      />
    );
  };

  const contents = [
    update.email === '[""]' || ""
      ? "Add email"
      : [update.email].join(", ").replace(/["[\]]/g, ""),
    !inputOpen ? (
      <Link to={update.url} target="_blank">
        {update.journal}
      </Link>
    ) : update.url === "" ? (
      "Add URL"
    ) : (
      update.url
    ),
    update.format,
    update.submissions,
    update.expedite,
    update.withdraw,
    update.librarians === "{}" ? "Add librarians" : update.librarians,
  ];

  const icons = [
    { iconName: "Mail", label: "Email" },
    { iconName: "Link", label: "Website" },
    { iconName: "PageHeaderEdit", label: "Format" },
    { iconName: "Send", label: "Submissions" },
    { iconName: "LightningBolt", label: "Expedite" },
    { iconName: "PageRemove", label: "Withdraw" },
    { iconName: "ReadingMode", label: "Librarians" },
  ];

  const getInitials = (name) => {
    const getFirst = name
      .replace(/[()of]/g, "")
      .toUpperCase()
      .split(" ")
      .map((i) => i[0])
      .join("");

    return getFirst;
  };

  return (
    <div
      className="crm-card"
      style={{ backgroundColor: activeItem ? "#598cfa" : "#4061d6" }}
    >
      <div className="crm-journal">
        <div className="crm-journal-header">
          {width > 1100 ? (
            item.journal.replace(/University/g, "U.")
          ) : (
            <TooltipHost content={item.journal}>
              {getInitials(item.journal)}
            </TooltipHost>
          )}
        </div>
        {width > 1000 ? (
          <div
            className="crm-journal-row"
            onClick={(e) => {
              e.preventDefault();
              setCollapsed(false);
            }}
          >
            {icons.map((icon, index) => (
              <TooltipHost content={icon.label} key={index}>
                <JournalIcon
                  iconName={icon.iconName}
                  label={icon.label}
                  index={index}
                />
              </TooltipHost>
            ))}
          </div>
        ) : (
          <div className="crm-journal-row">
            <FontIcon
              iconName="MoreVertical"
              className="crm-icons crm-journal-icons"
              ariaLabel="More"
              id={buttonId}
              onClick={toggleIsCalloutVisible}
            />
            {isCalloutVisible && (
              <Callout
                coverTarget
                ariaLabelledBy={labelId}
                role="dialog"
                onDismiss={toggleIsCalloutVisible}
                target={`#${buttonId}`}
                isBeakVisible={true}
                directionalHint={DirectionalHint.topRightEdge}
                setInitialFocus
                style={{
                  padding: 5,
                  width: 50,
                }}
              >
                <FontIcon
                  onClick={toggleIsCalloutVisible}
                  iconName="Cancel"
                  ariaLabel="Close popup"
                  style={{
                    fontSize: 14,
                    marginLeft: 20,
                    cursor: "pointer",
                    color: "var(--darkest-blue)",
                  }}
                />

                {icons.map((icon, index) => (
                  <TooltipHost content={icon.label} key={index}>
                    <FontIcon
                      iconName={icon.iconName}
                      className="crm-icons crm-journal-icons"
                      ariaLabel={icon.label}
                      id={index}
                      data-tooltip-id={icon.label}
                      onClick={handleChangeTab}
                      style={{
                        color: "var(--darkest-blue)",
                        fontWeight: 500,
                        "&:hover": { color: "var(--light-blue)" },
                      }}
                    />
                  </TooltipHost>
                ))}
              </Callout>
            )}
          </div>
        )}
      </div>
      <TooltipHost
        closeDelay={500}
        delay={0}
        content={
          inputOpen ? "Double click to cancel" : "Double click to update"
        }
      >
        <a.div
          style={spring}
          className="crm-card-content"
          onDoubleClick={(e) => {
            e.preventDefault();
            setInputOpen(!inputOpen);
          }}
        >
          {inputOpen ? (
            <>
              <TextField
                multiline
                name={items[activeTab]}
                placeholder={contents[activeTab]}
                value={contents[activeTab]}
                label={items[activeTab].replace(
                  /^./,
                  items[activeTab][0].toUpperCase()
                )}
                onChange={handleChange}
                onKeyDown={(e) => {
                  e.key === "Enter" && handleSubmit(e);
                }}
              />
              <div className="alert-footer">
                <div
                  className="alert-button"
                  id="dismiss-alert"
                  onClick={(e) => {
                    e.preventDefault();
                    setInputOpen(false);
                  }}
                >
                  Cancel
                </div>
                <div
                  className="alert-button"
                  style={{
                    backgroundColor: "var(--green)",
                  }}
                  onClick={handleSubmit}
                >
                  Save
                </div>
              </div>
            </>
          ) : (
            <div className="flex-row">
              {" "}
              <div className="crm-journal-sub">{items[activeTab]}:</div>{" "}
              <div>{contents[activeTab]}</div>
            </div>
          )}
        </a.div>
      </TooltipHost>
    </div>
  );
};

export default CRMJournalItem;
